import { useRef } from 'react'
import { queryOptions, useQueryClient } from '@tanstack/react-query'
import type { MentionOptions } from '@tiptap/extension-mention'
import { ReactRenderer } from '@tiptap/react'
import type { Suggestion } from 'shared/components/suggest-mention/types'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { communityKeys } from 'modules/community/api/key-factory'
import { getSuggestionsListKey } from 'modules/community/api/use-suggestions-list'
import {
  MentionSuggestionList,
  type MentionSuggestionListRef,
} from 'modules/community/components/editor/extensions/mention-suggestion-list'

interface CommunityRef {
  communityPath: string
  postId?: number
}

export const useMentionSuggestion = (
  getCommunity: () => CommunityRef | undefined,
): MentionOptions['suggestion'] => {
  const queryClient = useQueryClient()
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, Suggestion[]>({
    method: RequestMethodsEnum.get,
  })
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher

  return {
    char: '@',
    debounce: 300,
    dismissOnOutsideClick: false,
    allow: () => getCommunity() !== undefined,
    items: async ({ query: search }): Promise<Suggestion[]> => {
      const community = getCommunity()
      if (!community) return []
      const queryKey = communityKeys.mentionSuggestions(
        community.communityPath,
        community.postId,
        search || undefined,
      )
      try {
        return await queryClient.fetchQuery(
          queryOptions({
            queryKey,
            queryFn: async () =>
              (await fetcherRef.current(
                getSuggestionsListKey(
                  community.communityPath,
                  community.postId,
                  search || undefined,
                ),
              )) ?? [],
          }),
        )
      } catch {
        return []
      }
    },
    render: () => {
      let component: ReactRenderer<MentionSuggestionListRef> | null = null
      let unmount: (() => void) | undefined

      return {
        onStart: props => {
          component = new ReactRenderer(MentionSuggestionList, {
            props,
            editor: props.editor,
          })
          component.element.style.zIndex = '9999'
          unmount = props.mount(component.element)
        },
        onUpdate: props => component?.updateProps(props),
        onKeyDown: props => {
          if (props.event.key === 'Escape') return false
          return component?.ref?.onKeyDown(props) ?? false
        },
        onExit: () => {
          unmount?.()
          component?.destroy()
          component = null
          unmount = undefined
        },
      }
    },
  }
}
